import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plan, NewPlan, UpdatePlan } from '@fsp/types';
import { fetchPlans, fetchPlan, createPlan, updatePlan, deletePlan } from '../../services/plan';

// Query keys
export const planKeys = {
  all: ['plans'] as const,
  lists: () => [...planKeys.all, 'list'] as const,
  list: (orgId: string) => [...planKeys.lists(), orgId] as const,
  details: () => [...planKeys.all, 'detail'] as const,
  detail: (orgId: string, planId: string) => [...planKeys.details(), orgId, planId] as const,
};

// Queries
export const usePlans = (organisationId: string | undefined) => {
  return useQuery({
    queryKey: planKeys.list(organisationId!),
    queryFn: ({ signal }) => fetchPlans(organisationId!, signal),
    enabled: !!organisationId,
    placeholderData: (previousData) => previousData,
  });
};

export const usePlan = (organisationId: string | undefined, planId: string | undefined) => {
  return useQuery({
    queryKey: planKeys.detail(organisationId!, planId!),
    queryFn: ({ signal }) => fetchPlan(organisationId!, planId!, signal),
    enabled: !!organisationId && !!planId,
    placeholderData: (previousData) => previousData,
  });
};

// Mutations
export const useCreatePlan = (organisationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NewPlan) => createPlan(organisationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.list(organisationId) });
    },
  });
};

export const useUpdatePlan = (organisationId: string, planId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePlan) => updatePlan(organisationId, planId, data),
    onMutate: async (newData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: planKeys.detail(organisationId, planId) });

      // Snapshot previous value
      const previousPlan = queryClient.getQueryData<Plan>(planKeys.detail(organisationId, planId));

      // Optimistically update
      if (previousPlan) {
        queryClient.setQueryData<Plan>(planKeys.detail(organisationId, planId), {
          ...previousPlan,
          ...newData,
        });
      }

      return { previousPlan };
    },
    onError: (_err, _newData, context) => {
      // Rollback on error
      if (context?.previousPlan) {
        queryClient.setQueryData(planKeys.detail(organisationId, planId), context.previousPlan);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.detail(organisationId, planId) });
      queryClient.invalidateQueries({ queryKey: planKeys.list(organisationId) });
    },
  });
};

export const useDeletePlan = (organisationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => deletePlan(organisationId, planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.list(organisationId) });
    },
  });
};
